import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _attempt from 'lodash/attempt';

import StringWidget, { stringTemplate } from 'components/StringWidget/StringWidget';
import Button from 'components/Button';

import { PERMISSION_STATES } from 'utils/webAPIHelper';

import imgChrome from 'images/webpushchrome.png';
import imgArrow from 'images/webpusharrow.svg';
import imgLock from 'images/webpushlock.png';

import styles from './WebAPINotificationWidget.css';

export const STATES = {
	INTERNAL_ALLOW: 'INTERNAL_ALLOW',
	OVERLAY: 'OVERLAY',
	REACTIVE: 'REACTIVE',
	DISMISS: 'DISMISS'
};

export default class WebAPINotificationWidget extends Component {
	static propTypes = {
		prompt: PropTypes.bool,
		onMount: PropTypes.func,
		onAllow: PropTypes.func,
		promptMessage: PropTypes.string.isRequired,
		reactivateMessage: PropTypes.string.isRequired,
		overlayMessage: PropTypes.string.isRequired
	};

	static defaultProps = {
		prompt: false,
		promptMessage: '',
		reactivateMessage: '',
		overlayMessage: ''
	};

	constructor(props) {
		super(props);
		this.state = {
			currentView: props.prompt ? STATES.INTERNAL_ALLOW : STATES.DISMISS,
			dismissed: false
		};
	}

	static contextTypes = {
		modal: PropTypes.object.isRequired
	};

	componentDidMount() {
		const { registerPromptCallback } = this.props;
		typeof registerPromptCallback === 'function' && registerPromptCallback(this.promptForConfirmation);
	}

	startNewTimer = (callback, durationInMillisecond) => {
		if (this.timer) clearTimeout(this.timer);
		this.timer = setTimeout(callback, durationInMillisecond);
	};

	promptForConfirmation = (permissionState = PERMISSION_STATES.PROMPT) => {
		if (this.timer) clearTimeout(this.timer);
		switch (permissionState) {
			case PERMISSION_STATES.GRANTED:
				this.handleAllow();
				break;
			case PERMISSION_STATES.DENIED:
				this.handlePermissionDenied();
				break;
			default:
				this.setState({
					currentView: STATES.INTERNAL_ALLOW,
					dismissed: false
				});
				break;
		}
	};

	handleDismiss = timeInMiliseconds => {
		if (!timeInMiliseconds) {
			// executes set state immediately when timeInMilliseconds not specified
			this.setState({
				currentView: STATES.DISMISS,
				dismissed: true
			});
		} else {
			this.setState({ dismissed: false }, () => {
				this.startNewTimer(
					() =>
						this.setState({
							currentView: STATES.DISMISS,
							dismissed: true
						}),
					timeInMiliseconds
				);
			});
		}
	};

	handlePermissionDenied = () => {
		this.context.modal.hide();
		this.setState({ currentView: STATES.REACTIVE });
		this.handleDismiss(15000);
	};

	componentWillUnmount() {
		this.context.modal.hide();
		if (this.timer) clearTimeout(this.timer);
	}

	handleAllow = () => {
		const { onAllow } = this.props;
		this.setState({ currentView: STATES.OVERLAY }, () => {
			this.context.modal.show({
				children: this.renderOverlay(),
				classNames: {
					dialog: styles['notification-modal'],
					overlay: styles['notification-overlay']
				},
				closeOnEscape: false
			});
			_attempt(onAllow, this.handleDismiss, this.handlePermissionDenied);
		});
	};

	renderOverlay() {
		const { overlayMessage } = this.props;
		return (
			<div className={styles['notification-info']}>
				<img src={imgArrow} className={styles['notification-image']} />
				<StringWidget strKey="webAPINotificationAlmost" /> &nbsp;
				{stringTemplate('webAPINotificationOverlayMessage', {
					type: overlayMessage
				})}
			</div>
		);
	}

	renderPrompt() {
		const { promptMessage } = this.props;
		const { dismissed } = this.state;
		const classNames = cx(styles['notification-container'], {
			[styles['hide-notification']]: dismissed
		});
		return (
			<div className={classNames}>
				<div className={styles['notification-msg']}>
					<img src={imgChrome} className={styles['icon']} />
					<span className={styles['description']}>{promptMessage}</span>
				</div>
				<div className={styles['action-btns']}>
					<Button className={cx(styles['btn'], styles['dismiss-btn'])} onClick={this.handleDismiss}>
						{stringTemplate('notNowBtnText')}
					</Button>
					<Button primary className={cx(styles['btn'], styles['allow-btn'])} onClick={this.handleAllow}>
						{stringTemplate('okayBtnText')}
					</Button>
				</div>
			</div>
		);
	}

	renderReactivate() {
		const { reactivateMessage } = this.props;
		const { dismissed } = this.state;
		const classNames = cx(styles['notification-container'], styles['reactivate'], {
			[styles['hide-notification']]: dismissed
		});
		return (
			<div className={classNames}>
				<div className={cx(styles['notification-msg'], styles['reactivate'])}>
					{stringTemplate('webAPINotificationReactivateMsg')}
					<img src={imgLock} className={styles['lock-icon']} />
					{stringTemplate('webAPINotificationReactivateLockMsg', {
						type: reactivateMessage
					})}
				</div>
				<div className={styles['action-btns']}>
					<Button primary className={cx(styles['btn'], styles['reactivate-btn'])} onClick={this.handleDismiss}>
						{stringTemplate('webAPINotificationReactivateBtnTxt')}
					</Button>
				</div>
			</div>
		);
	}

	render() {
		switch (this.state.currentView) {
			case STATES.INTERNAL_ALLOW:
				return this.renderPrompt();
			case STATES.REACTIVE:
				return this.renderReactivate();
			default:
				return null;
		}
	}
}
